// VoiceOverSwitcher.jsx
import React, { useState } from "react";
import { observer } from "mobx-react-lite";
import { useStores } from "hooks";
import {
    VoiceSwitcherContainer,
    Title,
    VoiceOptionsContainer,
    VoiceOption,
    AvatarCircle,
    AvatarImg,
    VoiceDetails,
    VoiceName,
    VoiceDescription,
    SampleButton,
    ToggleRow,
    ToggleButton,
    ToggleDot,
    Icon,
    SettingsButton,
} from "./styles";
import AudioSettingsModal from "components/project/BidPanel/AudioSettingsModal";

import kyleAvatar from "assets/images/va_avatars/kyle.png";
import kenAvatar from "assets/images/va_avatars/ken.png";
import kendallAvatar from "assets/images/va_avatars/kendall.png";

export const VoiceOverSwitcher = observer(({ auctionId }) => {
    const { VirtualAuctionStore } = useStores();
    const [playingVoice, setPlayingVoice] = useState(null);
    const [settingsOpen, setSettingsOpen] = useState(false);

    const {
        setVoiceOver,
        voice,
        playVoiceOver,
        virtualLaneList,
        sfxEnabled,
        voiceEnabled,
        setSfxEnabled,
        setVoiceEnabled,
    } = VirtualAuctionStore;

    const voices = [
        {
            id: "kendall",
            name: "Kendall",
            description: "Female voice",
            avatar: kendallAvatar,
            sampleAudio: "sample.mp3",
        },
        {
            id: "kyle",
            name: "Kyle",
            description: "Male voice",
            avatar: kyleAvatar,
            sampleAudio: "sample.mp3",
        },
        {
            id: "ken",
            name: "Ken",
            description: "Male voice",
            avatar: kenAvatar,
            sampleAudio: "sample.mp3",
        },
    ];

    const handleVoiceChange = (voiceId) => {
        setVoiceOver(voiceId);
    };

    const playVoiceSample = async (e, id, file) => {
        e.stopPropagation();
        setPlayingVoice(id);
        try {
            await playVoiceOver(file, id);
        } finally {
            // Fall back to 3s cap in case onended doesn’t fire
            setTimeout(() => setPlayingVoice((cur) => (cur === id ? null : cur)), 3000);
        }
    };

    if (virtualLaneList.length === 0) return null;

    return (
        <VoiceSwitcherContainer>
            <Title>
                <Icon className="fa fa-microphone" /> Voice Selection
            </Title>

            {/* Compact global toggles */}
            <ToggleRow>
                <ToggleButton
                    aria-label={`SFX ${sfxEnabled ? "enabled" : "disabled"}`}
                    active={sfxEnabled}
                    onClick={(e) => {
                        e.stopPropagation();
                        setSfxEnabled(!sfxEnabled);
                    }}
                >
                    <span>SFX</span>
                    <ToggleDot active={sfxEnabled} />
                </ToggleButton>

                <ToggleButton
                    aria-label={`Voice ${voiceEnabled ? "enabled" : "disabled"}`}
                    active={voiceEnabled}
                    onClick={(e) => {
                        e.stopPropagation();
                        setVoiceEnabled(!voiceEnabled);
                    }}
                >
                    <span>Voice</span>
                    <ToggleDot active={voiceEnabled} />
                </ToggleButton>

                <SettingsButton
                    onClick={(e) => { e.stopPropagation(); setSettingsOpen(true); }}
                    aria-label="Audio settings"
                    title="Audio settings"
                >
                    <i className="fa fa-cog" />
                </SettingsButton>
            </ToggleRow>

            <AudioSettingsModal isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} />

            <VoiceOptionsContainer>
                {voices.map((v) => {
                    const isActive = voice === v.id;
                    const isPlaying = playingVoice === v.id;

                    return (
                        <VoiceOption
                            key={v.id}
                            isActive={isActive}
                            onClick={() => handleVoiceChange(v.id)}
                        >
                            <AvatarCircle isActive={isActive}>
                                <AvatarImg src={v.avatar} alt={`${v.name} avatar`} />
                            </AvatarCircle>

                            <VoiceDetails>
                                <VoiceName isActive={isActive}>{v.name}</VoiceName>
                                <VoiceDescription>{v.description}</VoiceDescription>
                            </VoiceDetails>

                            <SampleButton
                                type="button"
                                onClick={(e) => playVoiceSample(e, v.id, v.sampleAudio)}
                                isPlaying={isPlaying}
                                title="Play sample"
                            >
                                <Icon
                                    className={isPlaying ? "fa fa-volume-up" : "fa fa-headphones"}
                                />
                                <span>Sample</span>
                            </SampleButton>
                        </VoiceOption>
                    );
                })}
            </VoiceOptionsContainer>
        </VoiceSwitcherContainer>
    );
});

export default VoiceOverSwitcher;
